import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, Save, Trash2, ShieldCheck, ShieldOff, ChevronDown, ChevronRight, X } from "lucide-react";

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:"new"|"edit", key?, display_name, description, active, capabilities: Set}
  const [expanded, setExpanded] = useState(new Set());

  async function refresh() {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/admin/roles"),
        api.get("/admin/capabilities"),
      ]);
      setRoles(r.data || []);
      setCatalog(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  const capabilityLabel = useMemo(() => {
    const m = {};
    for (const grp of catalog) for (const [k, label] of grp.caps) m[k] = label;
    return m;
  }, [catalog]);

  function openNew() {
    setModal({
      mode: "new",
      display_name: "",
      description: "",
      active: true,
      capabilities: new Set(),
    });
  }
  function openEdit(r) {
    setModal({
      mode: "edit",
      key: r.key,
      display_name: r.display_name,
      description: r.description || "",
      active: r.active,
      is_system: r.is_system,
      capabilities: new Set(r.capabilities || []),
    });
  }

  function toggleCap(cap) {
    setModal((m) => {
      const next = new Set(m.capabilities);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return { ...m, capabilities: next };
    });
  }
  function toggleModuleAll(caps, on) {
    setModal((m) => {
      const next = new Set(m.capabilities);
      for (const [k] of caps) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return { ...m, capabilities: next };
    });
  }

  async function save() {
    try {
      const body = {
        display_name: modal.display_name.trim(),
        description: modal.description,
        active: modal.active,
        capabilities: Array.from(modal.capabilities),
      };
      if (modal.mode === "new") {
        await api.post("/admin/roles", body);
        toast.success("Rol creado");
      } else {
        await api.patch(`/admin/roles/${modal.key}`, body);
        toast.success("Rol actualizado");
      }
      setModal(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function toggleActive(r) {
    try {
      await api.patch(`/admin/roles/${r.key}`, { active: !r.active });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function del(r) {
    if (!confirm(`¿Eliminar el rol "${r.display_name}"? Esta acción es irreversible.`)) return;
    try {
      await api.delete(`/admin/roles/${r.key}`);
      toast.success("Rol eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function toggleExpand(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="admin-roles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Roles y permisos</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestión configurable de roles. Cada usuario hereda automáticamente los permisos de su rol.
          </p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="roles-new-button">
          <PlusCircle size={18} /> Nuevo rol
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono">Cargando…</div>
      ) : (
        <div className="space-y-3">
          {roles.map((r) => {
            const isOpen = expanded.has(r.key);
            return (
              <div key={r.key} className={`card-base p-4 ${!r.active ? "opacity-70" : ""}`} data-testid={`role-card-${r.key}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-bold text-lg">{r.display_name}</div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                        {r.key}
                      </span>
                      {r.is_system && (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                          Sistema
                        </span>
                      )}
                    </div>
                    {r.description && <div className="text-sm text-slate-600 mt-1">{r.description}</div>}
                    <div className="text-xs text-slate-500 font-mono mt-1">
                      {r.capabilities.length} permisos · {r.user_count} usuario(s)
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <button
                      onClick={() => toggleExpand(r.key)}
                      className="btn-ghost"
                      title={isOpen ? "Ocultar permisos" : "Ver permisos"}
                      data-testid={`role-toggle-${r.key}`}
                    >
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <button
                      onClick={() => toggleActive(r)}
                      className="btn-ghost"
                      title={r.active ? "Desactivar" : "Activar"}
                      disabled={r.is_system && r.key === "admin"}
                      data-testid={`role-active-${r.key}`}
                    >
                      {r.active ? <ShieldCheck size={16} className="text-green-600" /> : <ShieldOff size={16} className="text-red-500" />}
                    </button>
                    <button onClick={() => openEdit(r)} className="btn-ghost" data-testid={`role-edit-${r.key}`}>
                      Editar
                    </button>
                    {!r.is_system && (
                      <button onClick={() => del(r)} className="btn-ghost text-red-500" data-testid={`role-delete-${r.key}`}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid={`role-caps-${r.key}`}>
                    {catalog.map((grp) => {
                      const active = grp.caps.filter(([k]) => r.capabilities.includes(k));
                      if (active.length === 0) return null;
                      return (
                        <div key={grp.module}>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                            {grp.module}
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {active.map(([k, label]) => (
                              <span
                                key={k}
                                className="inline-block text-[11px] rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setModal(null)}
          role="dialog"
          data-testid="role-modal"
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-2xl w-full space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-extrabold text-xl">
                {modal.mode === "new" ? "Nuevo rol" : `Editar rol: ${modal.display_name}`}
              </h2>
              <button onClick={() => setModal(null)} className="btn-ghost">
                <X size={16} />
              </button>
            </div>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Nombre</span>
              <input
                value={modal.display_name}
                onChange={(e) => setModal({ ...modal, display_name: e.target.value })}
                className="input-base"
                placeholder="Ej: Vendedor Junior"
                data-testid="role-modal-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Descripción</span>
              <textarea
                value={modal.description || ""}
                onChange={(e) => setModal({ ...modal, description: e.target.value })}
                className="input-base"
                rows={2}
                placeholder="Alcance del rol…"
                data-testid="role-modal-description"
              />
            </label>
            <label className="flex items-center gap-2 select-none">
              <input
                type="checkbox"
                checked={!!modal.active}
                onChange={(e) => setModal({ ...modal, active: e.target.checked })}
                className="w-4 h-4"
                data-testid="role-modal-active"
                disabled={modal.mode === "edit" && modal.key === "admin"}
              />
              <span className="text-sm">Rol activo</span>
            </label>

            <div className="pt-2 border-t border-slate-200">
              <div className="text-sm font-heading font-bold text-slate-800 mb-2">Permisos</div>
              {catalog.map((grp) => {
                const groupCaps = grp.caps;
                const allOn = groupCaps.every(([k]) => modal.capabilities.has(k));
                return (
                  <div key={grp.module} className="mb-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-600 font-bold">
                        {grp.module}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleModuleAll(groupCaps, !allOn)}
                        className="text-[11px] font-mono text-blue-600 hover:underline"
                        data-testid={`role-module-toggle-${grp.module}`}
                      >
                        {allOn ? "Quitar todos" : "Marcar todos"}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1">
                      {groupCaps.map(([k, label]) => {
                        const on = modal.capabilities.has(k);
                        return (
                          <label
                            key={k}
                            className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 cursor-pointer text-sm select-none ${
                              on ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-700"
                            }`}
                            data-testid={`role-cap-${k}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleCap(k)}
                              className="w-4 h-4"
                              disabled={modal.mode === "edit" && modal.key === "admin"}
                            />
                            <span>{label}</span>
                            <span className="ml-auto text-[10px] font-mono text-slate-400">{k}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {modal.mode === "edit" && modal.key === "admin" && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  El rol <b>Administrador</b> siempre conserva todos los permisos.
                </div>
              )}
            </div>

            <button
              onClick={save}
              className="btn-primary w-full"
              data-testid="role-modal-save"
              disabled={!modal.display_name.trim()}
            >
              <Save size={16} /> Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
